// src/data/adminStore.ts
import { VISA_REQUIREMENTS_DATA, DestinationRequirement } from './visaRequirements';
import { safeGetItem, safeSetItem } from './storage';
import { Question, questionDefinitions, MALAYSIA_QUESTIONS, VIETNAM_QUESTIONS } from '../questions';

export interface HelperConfig {
  id: string;
  path: string;
  country: string;
  flag: string;
  category: string;
  types: string;
  processingTime: string;
  badge: string;
  badgeBg: string;
  bgGradient: string;
  isLocked: boolean;
  order: number;
}

export interface GuidelineTabConfig {
  id: string;
  label: string;
  isHidden?: boolean;
}

export interface AdminStoreConfig {
  exchangeRateLkr: number;
  hideUsdFromPublic: boolean;
  helpers: HelperConfig[];
  customRequirements: Record<string, DestinationRequirement>;
  guidelineTabs: GuidelineTabConfig[];
  countryGuidelineTabs?: Record<string, GuidelineTabConfig[]>;
  customQuestions?: Record<string, Question[]>;
}

export const INITIAL_HELPERS: HelperConfig[] = [
  {
    id: 'india',
    path: '/india',
    country: 'India',
    flag: '🇮🇳',
    category: 'mySky e-Visa Helper',
    types: 'Tourist (30D/1Y/5Y), Business, Medical, Conference',
    processingTime: '3 - 4 Business Days',
    badge: 'Popular',
    badgeBg: '#ef4444',
    bgGradient: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
    isLocked: false,
    order: 1,
  },
  {
    id: 'malaysia',
    path: '/malaysia',
    country: 'Malaysia',
    flag: '🇲🇾',
    category: 'e-VISA Social Visit Helper',
    types: 'Tourist 30 Days Single / Multiple Entry, Expatriate',
    processingTime: '2 - 3 Business Days',
    badge: 'Fast Track',
    badgeBg: '#10b981',
    bgGradient: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    isLocked: false,
    order: 2,
  },
  {
    id: 'vietnam',
    path: '/vietnam',
    country: 'Vietnam',
    flag: '🇻🇳',
    category: 'mySky e-Visa Helper',
    types: '30-Day & 90-Day Single / Multiple Entry e-Visa',
    processingTime: '3 - 5 Business Days',
    badge: 'New',
    badgeBg: '#f59e0b',
    bgGradient: 'linear-gradient(135deg, #991b1b 0%, #dc2626 100%)',
    isLocked: false,
    order: 3,
  },
];

export const INITIAL_GUIDELINE_TABS: GuidelineTabConfig[] = [
  { id: 'TOURIST', label: '🏝️ Tourist e-Visa Guidelines', isHidden: false },
  { id: 'BUSINESS', label: '💼 Business e-Visa Guidelines', isHidden: false },
  { id: 'MEDICAL', label: '🏥 Medical e-Visa Guidelines', isHidden: false },
];

export const DEFAULT_COUNTRY_GUIDELINE_TABS: Record<string, GuidelineTabConfig[]> = {
  INDIA: [
    { id: 'TOURIST', label: '🏝️ Tourist e-Visa Guidelines', isHidden: false },
    { id: 'BUSINESS', label: '💼 Business e-Visa Guidelines', isHidden: false },
    { id: 'MEDICAL', label: '🏥 Medical e-Visa Guidelines', isHidden: false },
  ],
  MALAYSIA: [
    { id: 'TOURIST', label: '🏝️ Tourist e-VISA Guidelines', isHidden: false },
    { id: 'BUSINESS', label: '💼 Business / Social Visit Guidelines', isHidden: false },
  ],
  VIETNAM: [
    { id: 'TOURIST', label: '🏝️ Tourist e-Visa Guidelines', isHidden: false },
    { id: 'BUSINESS', label: '💼 Business e-Visa Guidelines', isHidden: false },
  ],
};

export const getGuidelineTabsForCountry = (config: AdminStoreConfig, countryCode: string): GuidelineTabConfig[] => {
  const code = countryCode.toUpperCase();
  if (config.countryGuidelineTabs && config.countryGuidelineTabs[code]) {
    return config.countryGuidelineTabs[code];
  }
  return DEFAULT_COUNTRY_GUIDELINE_TABS[code] || config.guidelineTabs || INITIAL_GUIDELINE_TABS;
};

export const getQuestionsForCountry = (config: AdminStoreConfig, countryCode: string): Question[] => {
  const code = countryCode.toUpperCase();
  if (config.customQuestions && config.customQuestions[code]) {
    return config.customQuestions[code];
  }
  if (code === 'MALAYSIA') return MALAYSIA_QUESTIONS;
  if (code === 'VIETNAM') return VIETNAM_QUESTIONS;
  return questionDefinitions;
};

const STORAGE_KEY = 'mySkyAdminConfig';

export const loadAdminConfig = (): AdminStoreConfig => {
  try {
    const raw = safeGetItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);

      // Migration: Ensure 'Jul to Mar' description is updated to 'Multiple entry during peak tourist season'
      if (parsed.customRequirements?.INDIA?.visaPrices) {
        parsed.customRequirements.INDIA.visaPrices.forEach((p: any) => {
          if (p.name && p.name.includes('Jul to Mar')) {
            if (p.description.includes('Double Entry') || p.description.includes('Single/Double')) {
              p.description = 'Multiple entry during peak tourist season';
            }
          }
        });
      }

      // Migration: Force sync latest Business e-Visa checklist for India
      if (parsed.customRequirements?.INDIA) {
        if (!parsed.customRequirements.INDIA.categoryDocuments) {
          parsed.customRequirements.INDIA.categoryDocuments = JSON.parse(JSON.stringify(VISA_REQUIREMENTS_DATA.INDIA.categoryDocuments));
        } else {
          parsed.customRequirements.INDIA.categoryDocuments.BUSINESS = [
            ...VISA_REQUIREMENTS_DATA.INDIA.categoryDocuments!.BUSINESS
          ];
        }
      }

      // Migration: Force sync latest Tourist e-VISA checklist for Malaysia
      if (parsed.customRequirements?.MALAYSIA) {
        if (!parsed.customRequirements.MALAYSIA.categoryDocuments) {
          parsed.customRequirements.MALAYSIA.categoryDocuments = JSON.parse(JSON.stringify(VISA_REQUIREMENTS_DATA.MALAYSIA.categoryDocuments));
        } else {
          parsed.customRequirements.MALAYSIA.categoryDocuments.TOURIST = [
            ...VISA_REQUIREMENTS_DATA.MALAYSIA.categoryDocuments!.TOURIST
          ];
        }
      }

      // Migration: Force sync latest Tourist e-Visa checklist for Vietnam
      if (parsed.customRequirements?.VIETNAM) {
        if (!parsed.customRequirements.VIETNAM.categoryDocuments) {
          parsed.customRequirements.VIETNAM.categoryDocuments = JSON.parse(JSON.stringify(VISA_REQUIREMENTS_DATA.VIETNAM.categoryDocuments));
        } else {
          parsed.customRequirements.VIETNAM.categoryDocuments.TOURIST = [
            ...VISA_REQUIREMENTS_DATA.VIETNAM.categoryDocuments!.TOURIST
          ];
        }
      }

      return {
        exchangeRateLkr: parsed.exchangeRateLkr || 340,
        hideUsdFromPublic: parsed.hideUsdFromPublic !== undefined ? parsed.hideUsdFromPublic : true,
        helpers: parsed.helpers || INITIAL_HELPERS,
        customRequirements: parsed.customRequirements || VISA_REQUIREMENTS_DATA,
        guidelineTabs: parsed.guidelineTabs || INITIAL_GUIDELINE_TABS,
        countryGuidelineTabs: parsed.countryGuidelineTabs || DEFAULT_COUNTRY_GUIDELINE_TABS,
        customQuestions: parsed.customQuestions,
      };
    }
  } catch (err) {
    console.error('Failed to load admin config:', err);
  }
  return {
    exchangeRateLkr: 340,
    hideUsdFromPublic: true,
    helpers: INITIAL_HELPERS,
    customRequirements: VISA_REQUIREMENTS_DATA,
    guidelineTabs: INITIAL_GUIDELINE_TABS,
    countryGuidelineTabs: DEFAULT_COUNTRY_GUIDELINE_TABS,
  };
};

export const saveAdminConfig = (config: AdminStoreConfig) => {
  try {
    safeSetItem(STORAGE_KEY, JSON.stringify(config));
    window.dispatchEvent(new Event('adminConfigUpdated'));

    // Push to Cloudflare KV for global cross-device synchronization
    fetch('/api/admin-config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config),
    }).catch((err) => {
      console.warn('[adminStore] Cloudflare KV sync warning:', err);
    });
  } catch (err) {
    console.error('Failed to save admin config:', err);
  }
};

export const loadAdminConfigAsync = async (): Promise<AdminStoreConfig> => {
  const localConfig = loadAdminConfig();
  try {
    const res = await fetch('/api/admin-config');
    if (res.ok) {
      const json = await res.json();
      if (json && json.success && json.data) {
        safeSetItem(STORAGE_KEY, JSON.stringify(json.data));
        window.dispatchEvent(new Event('adminConfigUpdated'));
        return json.data;
      }
    }
  } catch (err) {
    console.warn('[adminStore] Could not fetch global KV config, using local config:', err);
  }
  return localConfig;
};

export const resetAdminConfigToDefaults = (): AdminStoreConfig => {
  const defaultConfig: AdminStoreConfig = {
    exchangeRateLkr: 340,
    hideUsdFromPublic: true,
    helpers: INITIAL_HELPERS,
    customRequirements: VISA_REQUIREMENTS_DATA,
    guidelineTabs: INITIAL_GUIDELINE_TABS,
    countryGuidelineTabs: DEFAULT_COUNTRY_GUIDELINE_TABS,
  };
  saveAdminConfig(defaultConfig);
  return defaultConfig;
};

// Automatically fetch global Cloudflare KV admin config on module initialization
if (typeof window !== 'undefined') {
  setTimeout(() => {
    loadAdminConfigAsync();
  }, 100);
}
